import { folderLabel } from "@/domains/documents/utils/briefFolder";
import { documentGridTemplate } from "@/domains/documents/utils/fitDocumentColumns";
import { HIGHLIGHTED_ROW_CLASS, useDocumentRowDecorations } from "./documentRowDecorations";
import { RowChipBadge } from "./RowChipBadge";
import { VersionBadge } from "@/components/ui/documents/version-badge";
import { FolderIcon } from "../atoms/FolderIcon";
import { FolderName } from "../atoms/FolderName";
import { DocumentCheckbox } from "../molecules/DocumentCheckbox";
import { FolderActionMenu } from "../molecules/FolderTree/FolderActionMenu";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { TreeFolder } from "../../types";

/** A versioned folder's badge in the Version column (the Brief folder's versions). */
export interface FolderVersionBadge {
  latestVersionNo: number;
  isExpanded: boolean;
  onToggle: () => void;
  ariaLabel: string;
}

interface FolderTableRowProps {
  /** Folder data */
  folder: TreeFolder;
  /** Current selection state */
  isSelected?: boolean;
  /** Whether folder name is being edited */
  isEditing?: boolean;
  /** Show/hide Checkbox column */
  showCheckbox?: boolean;
  /** Show/hide Version column (empty for folders) */
  showVersion?: boolean;
  /**
   * A versioned folder's badge. The row then takes the document rows' columns so
   * the badge sits in their Version column; other folders span the full width.
   */
  version?: FolderVersionBadge;
  /** Show/hide Date Created column (empty for folders) */
  showDateCreated?: boolean;
  /** Show/hide Status column (empty for folders) */
  showStatus?: boolean;
  /** Show/hide User column (empty for folders) */
  showUser?: boolean;
  /** Show/hide Tags column (empty for folders) */
  showTags?: boolean;
  /** Show/hide Parent column (empty for folders) */
  showParent?: boolean;
  /** Show/hide Actions column */
  showActions?: boolean;
  /** Callback when folder is selected */
  onSelect?: (isSelected: boolean) => void;
  /** Callback when folder is clicked to navigate into */
  onClick?: () => void;
  /** Callback when Rename is picked in the actions menu. Starts inline editing —
   *  it must NOT submit a rename (the row has no new name at that point).
   *  Pass undefined to hide the Rename action. */
  onRename?: () => void;
  /** Callback when folder is deleted */
  onDelete?: () => void;
  /** Callback when folder export (zip download) is triggered */
  onExport?: () => void;
  /** Called when "Organize with AI" is selected (WHA-3654) */
  onOrganize?: () => void;
  /** Callback when folder share is triggered */
  onShare?: () => void;
  /** Callback when subfolder is created */
  onCreateSubfolder?: () => void;
  /** Current inline-edit value. Falls back to the folder's own name so the input
   *  is never uncontrolled when a caller renders `isEditing` without edit state. */
  editName?: string;
  /** Name change during editing */
  onNameChange?: (value: string) => void;
  /** Confirm name change */
  onNameConfirm?: (value: string) => void;
  /** Cancel name editing */
  onNameCancel?: () => void;
  /** Delete button label */
  deleteLabel?: string;
  /** Additional CSS classes */
  className?: string;
  /** Capture-phase click handler for range/modifier selection. Runs before the
   *  inner checkbox's onClick so shift/ctrl clicks can stopPropagation and prevent
   *  Radix from toggling the row. */
  onRowClickCapture?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** Native move support, enabled only on the project Folders surface. */
  isDraggable?: boolean;
  isDragging?: boolean;
  isDropAvailable?: boolean;
  isDragOver?: boolean;
  onDragStart?: React.DragEventHandler<HTMLDivElement>;
  onDragEnd?: React.DragEventHandler<HTMLDivElement>;
  onDragEnter?: React.DragEventHandler<HTMLDivElement>;
  onDragOver?: React.DragEventHandler<HTMLDivElement>;
  onDragLeave?: React.DragEventHandler<HTMLDivElement>;
  onDrop?: React.DragEventHandler<HTMLDivElement>;
}

/**
 * FolderTableRow - Organism component
 *
 * Displays a folder row within the document list table.
 * Uses the same grid layout as DocumentTableRow for alignment.
 * Folder rows appear above document rows when displaying folder contents.
 *
 * Uses theme colors from the existing design system.
 *
 * @example
 * ```tsx
 * <FolderTableRow
 *   folder={folder}
 *   onClick={() => navigateToFolder(folder.id)}
 *   onDelete={() => deleteFolder(folder.id)}
 * />
 * ```
 */
export function FolderTableRow({
  folder,
  isSelected = false,
  isEditing = false,
  showCheckbox = true,
  showVersion = true,
  version,
  showDateCreated = true,
  showStatus = true,
  showUser = true,
  showTags = true,
  showParent = false,
  showActions = true,
  onSelect,
  onClick,
  onRename,
  onDelete,
  onExport,
  onOrganize,
  onShare,
  onCreateSubfolder,
  editName,
  onNameChange,
  onNameConfirm,
  onNameCancel,
  deleteLabel = "Delete",
  className,
  onRowClickCapture,
  isDraggable = false,
  isDragging = false,
  isDropAvailable = false,
  isDragOver = false,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
}: FolderTableRowProps) {
  const { t } = useLanguage();

  // Folder rows use a simple 3-column layout: checkbox | content | actions
  // This allows the folder name to span the full width instead of matching document columns.
  // A versioned folder takes the document columns instead (must match
  // DocumentTableRow / DocumentListHeader), so its badge lines up with theirs.
  const withVersion = !!version && showVersion;
  const gridColumns = withVersion
    ? documentGridTemplate({
        showCheckbox,
        showDateCreated,
        showUser,
        showVersion: true,
        showStatus,
        showTags,
        showParent,
        showActions,
      })
    : [
        showCheckbox && "28px", // Checkbox — must match DocumentTableRow / DocumentListHeader
        "1fr", // Content (spans full width)
        showActions && "50px", // Actions
      ]
        .filter(Boolean)
        .join(" ");
  const label = folderLabel(folder);

  // Title click → navigate into the folder. Bails while editing the name.
  // Modifier keys are handled by the parent's onRowClickCapture (range/toggle select).
  const decorations = useDocumentRowDecorations();
  const hostChip = decorations?.folderChip?.(String(folder.id)) ?? null;
  const hostHighlighted = decorations?.isHighlighted?.("folder", String(folder.id)) ?? false;

  const handleTitleClick = (e: React.MouseEvent) => {
    if (isEditing || !onClick) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.stopPropagation();
    onClick();
  };

  return (
    <div
      className={cn(
        "group grid select-none gap-4 border-b px-6 py-2.5 transition-colors",
        isSelected ? "border-primary/50 bg-primary/5 hover:bg-primary/10" : "hover:bg-[#f7f7f7]",
        isDraggable && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50",
        isDropAvailable && "border-blue-200 bg-blue-50/40 ring-1 ring-inset ring-blue-200",
        isDragOver && "relative z-10 bg-blue-100 ring-2 ring-inset ring-blue-500",
        hostHighlighted && !isDragOver && HIGHLIGHTED_ROW_CLASS,
        className
      )}
      data-highlighted={hostHighlighted || undefined}
      style={{
        gridTemplateColumns: gridColumns,
      }}
      onClickCapture={onRowClickCapture}
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-drop-available={isDropAvailable || undefined}
    >
      {/* Checkbox */}
      {showCheckbox && (
        <DocumentCheckbox isSelected={isSelected} onSelect={(selected) => onSelect?.(selected)} />
      )}

      {/* Folder Icon + Name with document count - spans full width */}
      <div className="flex min-w-0 gap-3">
        <div className="flex-shrink-0 pt-1">
          <FolderIcon isOpen={false} variant={folder.variant} className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          {/* Only the folder-name text opens the folder. The trigger is sized
              to its content (inline-block) so clicks on the empty space beside
              the name fall through to the row's selection handler — matching
              how document rows behave. While editing, the input keeps the
              plain full-width wrapper. */}
          {isEditing ? (
            <div data-row-title>
              <FolderName
                name={editName ?? folder.name}
                isEditing
                onChange={onNameChange}
                onConfirm={onNameConfirm}
                onCancel={onNameCancel}
                className="text-sm font-medium"
              />
            </div>
          ) : onClick ? (
            <button
              type="button"
              data-row-title
              onClick={handleTitleClick}
              className="inline-block max-w-full cursor-pointer text-left align-bottom hover:underline"
            >
              <FolderName name={label} isEditing={false} className="text-sm font-medium" />
            </button>
          ) : (
            <div data-row-title className="inline-block max-w-full">
              <FolderName name={label} isEditing={false} className="text-sm font-medium" />
            </div>
          )}
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span>
              {t.documents.folder.documentCount.replace(
                "{count}",
                String(folder.documentCount || 0)
              )}
            </span>
            {hostChip && <RowChipBadge chip={hostChip} />}
            {folder.children && folder.children.length > 0 && (
              <>
                <span>•</span>
                <span>
                  {t.documents.folder.subfolderCount.replace(
                    "{count}",
                    String(folder.children.length)
                  )}
                </span>
              </>
            )}
            {isDropAvailable && (
              <>
                <span>•</span>
                <span className="font-medium text-blue-700">
                  {isDragOver
                    ? t.documents.folder.releaseToMove
                    : t.documents.folder.dropHereToMove}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {withVersion && (
        <>
          {showDateCreated && <div />}
          {showUser && <div />}
          <div className="flex items-center justify-center">
            <VersionBadge
              version={version.latestVersionNo}
              hasHistory
              isExpanded={version.isExpanded}
              onClick={version.onToggle}
              ariaLabel={version.ariaLabel}
            />
          </div>
          {showStatus && <div />}
          {showTags && <div />}
          {showParent && <div />}
        </>
      )}

      {/* Actions */}
      {showActions && (
        <div className="flex justify-end" data-actions onClick={(e) => e.stopPropagation()}>
          <div className="opacity-0 transition-opacity group-hover:opacity-100">
            <FolderActionMenu
              onCreateSubfolder={onCreateSubfolder}
              onRename={onRename}
              onDelete={onDelete}
              onExport={onExport}
              onOrganize={onOrganize}
              onShare={onShare}
              deleteLabel={deleteLabel}
            />
          </div>
        </div>
      )}
    </div>
  );
}
